import { Play, ExternalLink, Clock, Trash2 } from "lucide-react";
import { CATEGORY_IMG } from "@/lib/assets";

const GRADS = {
  kemetic: "from-yellow-700 to-amber-950", nuwaupian: "from-amber-600 to-yellow-950",
  noi: "from-red-800 to-red-950", moorish: "from-orange-700 to-amber-950",
  hebrew: "from-blue-900 to-indigo-950", metaphysical: "from-purple-900 to-fuchsia-950",
  panafrican: "from-green-800 to-emerald-950", esoteric: "from-neutral-700 to-stone-950",
  astro: "from-indigo-900 to-slate-950", herbal: "from-green-800 to-lime-950",
};

function Thumb({ video, grad, catImg, initials, className }) {
  return (
    <div className={`relative bg-gradient-to-br ${grad} flex items-center justify-center overflow-hidden ${className}`}>
      {video.youtube_id ? (
        <img src={`https://img.youtube.com/vi/${video.youtube_id}/hqdefault.jpg`} alt={video.title} className="absolute inset-0 w-full h-full object-cover opacity-80" />
      ) : (
        <>
          {catImg && <img src={catImg} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-55 group-hover:scale-105 transition-all duration-500" />}
          <span className="relative font-serif text-4xl text-white/40 select-none drop-shadow-lg">{initials}</span>
        </>
      )}
      <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition-colors flex items-center justify-center">
        <div className="w-12 h-12 rounded-full bg-gold/90 flex items-center justify-center scale-90 group-hover:scale-100 transition-transform">
          <Play className="text-black ml-0.5" fill="black" size={18} />
        </div>
      </div>
      {video.duration && <span className="absolute bottom-2 right-2 text-[11px] bg-black/70 text-bone px-2 py-0.5 flex items-center gap-1 font-mono"><Clock size={11} />{video.duration}</span>}
    </div>
  );
}

export function VideoListItem({ video, onPlay, onDelete }) {
  const grad = GRADS[video.category] || "from-neutral-800 to-black";
  const catImg = CATEGORY_IMG[video.category];
  const initials = (video.teacher || "?").split(" ").map((w) => w[0]).slice(0, 2).join("");
  return (
    <div className="group bg-surface border border-white/10 hover:border-gold/50 transition-colors flex items-stretch gap-4 p-3" data-testid="video-list-item">
      <button onClick={() => onPlay(video)} className="relative block shrink-0 w-40 sm:w-52 text-left" data-testid="video-play-button">
        <Thumb video={video} grad={grad} catImg={catImg} initials={initials} className="aspect-video w-full" />
      </button>
      <div className="flex-1 min-w-0 flex flex-col justify-center py-1">
        <p className="text-xs uppercase tracking-widest text-gold mb-1">{video.teacher}</p>
        <button onClick={() => onPlay(video)} className="text-left" data-testid="video-list-title">
          <h3 className="font-serif text-lg sm:text-xl leading-snug text-bone line-clamp-2 hover:text-gold transition-colors">{video.title}</h3>
        </button>
        {video.description && <p className="text-bone/45 text-sm mt-1 line-clamp-1 hidden sm:block">{video.description}</p>}
        <div className="flex items-center gap-4 text-xs text-bone/40 mt-2">
          <span>{video.views?.toLocaleString()} views</span>
          <a href={video.external_url} target="_blank" rel="noreferrer" className="hover:text-gold flex items-center gap-1" onClick={(e) => e.stopPropagation()} data-testid="video-external-link"><ExternalLink size={13} /> Open</a>
          {onDelete && <button onClick={() => onDelete(video.id)} className="hover:text-red-400 flex items-center gap-1" data-testid="video-delete-button"><Trash2 size={13} /> Delete</button>}
        </div>
      </div>
    </div>
  );
}

export default function VideoCard({ video, onPlay, onDelete }) {
  const grad = GRADS[video.category] || "from-neutral-800 to-black";
  const catImg = CATEGORY_IMG[video.category];
  const initials = (video.teacher || "?").split(" ").map((w) => w[0]).slice(0, 2).join("");
  return (
    <div className="group bg-surface border border-white/10 hover:border-gold/50 transition-colors overflow-hidden" data-testid="video-card">
      <button onClick={() => onPlay(video)} className="relative block w-full text-left" data-testid="video-play-button">
        <div className={`relative aspect-video bg-gradient-to-br ${grad} flex items-center justify-center overflow-hidden`}>
          {video.youtube_id ? (
            <img src={`https://img.youtube.com/vi/${video.youtube_id}/hqdefault.jpg`} alt={video.title} className="absolute inset-0 w-full h-full object-cover opacity-80" />
          ) : (
            <>
              {catImg && <img src={catImg} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-55 group-hover:scale-105 transition-all duration-500" />}
              <span className="relative font-serif text-5xl text-white/40 select-none drop-shadow-lg">{initials}</span>
            </>
          )}
          <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition-colors flex items-center justify-center">
            <div className="w-14 h-14 rounded-full bg-gold/90 flex items-center justify-center scale-90 group-hover:scale-100 transition-transform">
              <Play className="text-black ml-1" fill="black" size={22} />
            </div>
          </div>
          <span className="absolute bottom-2 right-2 text-[11px] bg-black/70 text-bone px-2 py-0.5 flex items-center gap-1 font-mono"><Clock size={11} />{video.duration}</span>
        </div>
      </button>
      <div className="p-4">
        <p className="text-xs uppercase tracking-widest text-gold mb-1.5">{video.teacher}</p>
        <h3 className="font-serif text-lg leading-snug text-bone line-clamp-2 mb-2">{video.title}</h3>
        <div className="flex items-center justify-between text-xs text-bone/40">
          <span>{video.views?.toLocaleString()} views</span>
          <div className="flex items-center gap-3">
            <a href={video.external_url} target="_blank" rel="noreferrer" className="hover:text-gold" onClick={(e) => e.stopPropagation()} data-testid="video-external-link"><ExternalLink size={14} /></a>
            {onDelete && <button onClick={() => onDelete(video.id)} className="hover:text-red-400" data-testid="video-delete-button"><Trash2 size={14} /></button>}
          </div>
        </div>
      </div>
    </div>
  );
}
